// src/scenes/MultiplayerGameOverScene.js
import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js"; // Import the base scene
import MenuUIUtils from "../ui/MenuUIUtils.js"; // Import UI Utils
import { auth, db, firebase } from '../firebaseConfig.js';
import claudeAPIService from "../services/claudeAPIService.js";
import sceneManager from "../utils/SceneManager.js";
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from '../services/specialAccessService.js';

// Constants for Depths - More clearly defined with larger gaps to avoid overlap
const DEPTHS = {
    BACKGROUND: -10,
    BACKGROUND_PARTICLES: -5,
    PANEL_SHADOW: 4,
    PANEL_BACKGROUND: 5,
    HEADER_BACKGROUND: 7,
    ROW_BACKGROUND: 9,
    TEXT: 10,
    HIGHLIGHT_GLOW: 15,
    ACHIEVEMENT_PANEL: 20, // Ensure Achievement panel is above leaderboards if needed
    ACHIEVEMENT_GLOW: 19,
    ACHIEVEMENT_TEXT: 21,
    BUTTONS: 30,
    OVERLAYS: 100,
};

export default class MultiplayerGameOverScene extends BaseMenuScene {
    constructor() {
        super({ key: 'MultiplayerGameOverScene' });
        this.claudeApi = new claudeAPIService();
        this.elements = {}; // Store UI elements for easy access/cleanup
        this.userId = '';
        this.playerDisplayName = 'Player';
        this.topTeamScores = [];
        this.topSoloScores = [];
        this.topIndividualScores = []; // Combined solo + team for all-time display if needed
        this.bgParticles = []; // Ensure clean removal
        this.bgGrid = null;    // Ensure clean removal
        this.isSinglePlayer = false;
        this.score = 0;
        this.gameData = null; // Raw data from MP game end if available
        this.gameCode = null;
        this.playerScores = []; // Scores from the *current* MP game
        this.teamName = "";
        this.playerRank = null; // Rank in all-time solo leaderboard
        this.totalPlayers = 0; // Total players on all-time solo leaderboard
        this.gameType = "Shortcut"; // Default game type key
        this.gameOverTitle = "PROCESSING...";
        this.playerAchievement = { name: "ANALYZING...", description: "Calculating performance..." };
        this.achievementPulseEffect = null;
        this.updateTimer = null; // Timer for MP score updates (if implemented)

        // Layout related
        this.contentStartY = 100; // Default start Y for content panels
        this.padding = 20; // Inherited, but useful locally
    }

    init(data) {
        super.init(data); // Call BaseMenuScene init
        console.log("[GameOverScene] Initializing with data:", data);
        this.score = data.score || 0;
        this.gameData = data.gameData || null; // Might contain teamName, mode etc.
        this.gameCode = data.gameCode || null;
        this.playerScores = data.playerScores || []; // Scores from the current multiplayer match
        this.teamName = data.teamName || (this.gameData?.teamName) || ""; // Use passed, gameData, or default

        this.gameType = data.gameType || "Shortcut"; // e.g., "Shortcut", "PPTHero", "MultiplayerShortcut"
        // Determine if it was effectively a single-player game session
        this.isSinglePlayer = this.gameType === "Shortcut" || this.gameType === "PPTHero" || (this.playerScores && this.playerScores.length <= 1 && !this.gameCode);
        console.log(`[GameOverScene] Mode determined as: ${this.isSinglePlayer ? 'Single Player' : 'Multiplayer'}`);

        // Determine Player Identity
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        if (user) {
            this.userId = user.uid;
            // Use passed name first, then profile, then auth, then fallback
            this.playerDisplayName = data.playerDisplayName || user.displayName || user.email || 'Player';
            // Async fetch profile name to potentially update later (don't block init)
            db.collection("userProfiles").doc(user.uid).get().then(doc => {
                if(doc.exists && doc.data().displayName) this.playerDisplayName = doc.data().displayName;
            }).catch(e => console.warn("Error fetching profile name in init:", e));
        } else if (specialAccess) {
            this.userId = getSpecialAccessUserId();
            this.playerDisplayName = getSpecialAccessDisplayName();
        } else {
            this.playerDisplayName = localStorage.getItem("tempSimpsonsName") || `Guest-${Math.floor(Math.random()*10000)}`;
            this.userId = `guest_${this.playerDisplayName}`; // Assign a guest ID
             if (!localStorage.getItem("tempSimpsonsName")) {
                localStorage.setItem("tempSimpsonsName", this.playerDisplayName);
            }
        }

        this.playerRank = data.playerRank; // May be null/undefined initially
        this.totalPlayers = data.totalPlayers || 0; // May be 0 initially

        // Reset dynamic elements
        this.gameOverTitle = "PROCESSING...";
        this.playerAchievement = { name: "ANALYZING...", description: "Calculating performance..." };
        this.elements = {}; // Clear previous UI elements
        this.topTeamScores = [];
        this.topSoloScores = [];
        this.topIndividualScores = [];
        this.bgParticles = [];
        this.bgGrid = null;
        this.achievementPulseEffect = null;
        if (this.updateTimer) { this.updateTimer.remove(); this.updateTimer = null; }

        console.log(`[GameOverScene] User: ${this.playerDisplayName} (ID: ${this.userId})`);
    }

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire create method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire create method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire create method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire create method with this:
async create() {
  super.create();
  console.log(`[GameOverScene] Create START (Mode: ${this.isSinglePlayer ? "Single" : "Multi"})`);

  try {
       if (!this.cameras || !this.cameras.main) throw new Error("Camera not available in create");

      const width = this.cameras.main.width;
      const height = this.cameras.main.height;
      const centerX = width / 2;
      const scenePadding = this.padding || 20;
      const headerHeight = Math.min(100, height * 0.12);
      this.contentStartY = headerHeight + scenePadding;

      // --- Create Main Containers ---
      this.elements.leaderboardContainer = this.add.container(0, this.contentStartY).setDepth(DEPTHS.PANEL_BACKGROUND);
      this.elements.contentContainer = this.add.container(0, 0);
      this.elements.achievementContainer = this.add.container(0, 0).setDepth(DEPTHS.ACHIEVEMENT_PANEL).setVisible(false);
      console.log("[GameOverScene] Core containers created.");

      // --- Header Title (Enhanced Style) ---
      const titleStyle = {
          fontFamily: 'monospace',
          // Increase size slightly for more impact
          fontSize: '36px', // Was '32px'
          fontStyle: 'bold',
          // Use a gradient fill for visual flair
          fill: '#ffffff', // Fallback white
          // stroke: '#000000', // Can keep stroke or adjust shadow
          // strokeThickness: 3,
          // Enhanced Shadow for pop
          shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 8, stroke: true, fill: true }, // Stronger shadow
          align: 'center'
      };
      this.elements.headerText = this.add.text(centerX, headerHeight * 0.5, this.gameOverTitle, titleStyle)
          .setOrigin(0.5)
          .setDepth(DEPTHS.TEXT + 5);

      // Apply Gradient Fill (if supported)
      const gradient = this.elements.headerText.context.createLinearGradient(0, 0, 0, this.elements.headerText.height);
      gradient.addColorStop(0, '#00FF00'); // Neon Green top
      gradient.addColorStop(0.5, '#66FF66'); // Lighter Green middle
      gradient.addColorStop(1, '#00DD00'); // Slightly darker Green bottom
      this.elements.headerText.setFill(gradient);
      // --- End Header Title ---


      // Loading Text
      this.elements.loadingText = this.createThemedText(centerX, this.contentStartY + 50, "Loading results...", 'sizeLg', MenuUIUtils.COLORS.warning);
      this.add.existing(this.elements.loadingText);

      // Await data loading and layout creation
      await this.loadScoresAndLayout(this, this.contentStartY, height - this.contentStartY - 100);

      // Remove loading indicator
      if (this.elements.loadingText) { this.elements.loadingText.destroy(); delete this.elements.loadingText; }

      this.handleResize();

  } catch (e) {
      console.error("[GameOverScene] CRITICAL error in create:", e);
      if (this.elements.loadingText) { try { this.elements.loadingText.destroy(); } catch(e){} delete this.elements.loadingText; }
      this.fallbackGameOverDisplay("ERROR CREATING SCENE");
  }
  console.log("[GameOverScene] Create FINISHED");
}
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createProfessionalBackground method with this:
createProfessionalBackground() {
  // This method is actually static on MenuUIUtils and called by BaseMenuScene's createBackground
  // We keep this structure consistent with the previous response, but the actual call site was wrong.
  // No need to call this directly.
  console.warn("createProfessionalBackground called directly, but should be handled by BaseMenuScene.create()");
  // Optionally, delegate to the static method if needed for some specific reason:
  // return MenuUIUtils.createProfessionalBackground(this, this.backgroundOptions);
}

// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createTabButton method with this:
// Helper to create Tab Buttons for MP Layout
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createTabButton method with this:
createTabButton(container, x, y, label, key) {
  if (!container || !container.scene) {
       console.error("Invalid container passed to createTabButton for label:", label);
       return { setActive: () => {}, on: () => {}, setInteractive: () => {}, disableInteractive: () => {}, getData: () => null, setData: () => {}, width: 0, height: 0 };
  }
   const scene = container.scene;
   const btn = MenuUIUtils.createStyledButton(scene, x, y, label, () => {}, { // Pass scene context
       bgColor: MenuUIUtils.COLORS.bgLight,
       textColor: MenuUIUtils.COLORS.textMedium,
       hoverColor: MenuUIUtils.COLORS.bgMedium,
       width: Math.min(200, (scene.cameras.main.width * 0.8) / 2 - 10),
       height: 40,
       fontSize: '16px'
   });

   // --- ADD GUARD for null button ---
   if (!btn) {
        console.error(`Failed to create tab button for label "${label}"`);
        return { setActive: () => {}, on: () => {}, setInteractive: () => {}, disableInteractive: () => {}, getData: () => null, setData: () => {}, width: 0, height: 0 };
   }
   // --- END GUARD ---

   btn.setActive = (isActive) => {
       const bg = btn.getData('rectGraphic');
       const text = btn.getData('textObject');
       if (!bg || !text) {
           console.warn(`Cannot set active state for "${label}": button graphics or text missing.`);
           return;
       }

       if (isActive) {
           bg.clear().fillStyle(MenuUIUtils.COLORS.secondary, 1).fillRoundedRect(-btn.width/2, -btn.height/2, btn.width, btn.height, MenuUIUtils.SPACING.xs);
           text.setColor(Phaser.Display.Color.ValueToColor(MenuUIUtils.COLORS.bgDark).rgba);
           if(btn.input) btn.disableInteractive();
       } else {
           bg.clear().fillStyle(MenuUIUtils.COLORS.bgLight, 1).fillRoundedRect(-btn.width/2, -btn.height/2, btn.width, btn.height, MenuUIUtils.SPACING.xs);
           text.setColor(Phaser.Display.Color.ValueToColor(MenuUIUtils.COLORS.textMedium).rgba);
           if(btn.input) btn.setInteractive({ useHandCursor: true });
       }
   };

   container.add(btn);
   return btn;
}
    // --- Layout Creation Logic ---

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire loadScoresAndLayout method with this:
async loadScoresAndLayout(sceneContext, contentStartY, availableContentHeight) { // Accept sceneContext
  console.log(`[GameOverScene] loadScoresAndLayout START`);
  if (!sceneContext || !sceneContext.scene?.isActive) {
      console.error("[GameOverScene] Invalid scene context in loadScoresAndLayout.");
      return;
  }
  try {
      if (this.isSinglePlayer) {
          const spGameTypeKey = (this.gameType === "PPTHero") ? "PPTHero" : "Shortcut";
          await this.fetchTopIndividualScores(10, spGameTypeKey, false);
          // Pass sceneContext to the layout function
          this.createSPSinglePlayerLayout(sceneContext, contentStartY, availableContentHeight);
          this.gameOverTitle = "ROUND COMPLETE";
      } else {
          await Promise.all([
              this.fetchTopTeamScores(10),
              this.fetchTopIndividualScores(10, "MultiplayerShortcut", false),
              this.generateAiContent()
          ]);
          // Pass sceneContext to the layout function
          this.createMPLayout(sceneContext, contentStartY, availableContentHeight);
      }

      // Update header text AFTER AI content might have set it
      if (this.elements.headerText) this.elements.headerText.setText(this.gameOverTitle);

  } catch (error) {
      console.error("[GameOverScene] Error loading scores or generating content:", error);
      if (sceneContext.elements.leaderboardContainer) {
           sceneContext.elements.leaderboardContainer.removeAll(true);
           sceneContext.elements.leaderboardContainer.add(
               this.createThemedText(sceneContext.cameras.main.width / 2, contentStartY + 50, "Error loading results.", 'sizeLg', MenuUIUtils.COLORS.danger)
           );
           this.createReturnButton(contentStartY + 150); // Ensure 'this' context is correct or pass sceneContext
      } else {
           this.fallbackGameOverDisplay("ERROR LOADING DATA");
      }
  }
  console.log("[GameOverScene] loadScoresAndLayout FINISHED");
}

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createSPSinglePlayerLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createSPSinglePlayerLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createSPSinglePlayerLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createSPSinglePlayerLayout method with this:
createSPSinglePlayerLayout(sceneContext, contentStartY, maxHeight) {
  const logPrefix = "[GameOverScene-SP]";
  console.log(`${logPrefix} Creating Single Player Layout`);

  if (!sceneContext || !sceneContext.elements.leaderboardContainer || !sceneContext.add || !sceneContext.cameras?.main) {
      console.error(`${logPrefix} Invalid scene context or leaderboard container.`); return;
  }
  const mainContainer = sceneContext.elements.leaderboardContainer;
  mainContainer.removeAll(true); // Clear previous content

  const width = sceneContext.cameras.main.width;
  const centerX = width / 2;
  const panelWidth = Math.min(600, width * 0.85);
  let currentY = 0; // Y relative to mainContainer (which starts at contentStartY)
  const panelPadding = 20;
  const sectionGap = 25;
  const tableBottomPadding = 15; // Extra padding below the table/rank info

  // --- Main Panel Background (Sized at the end) ---
  const panelBg = sceneContext.add.rectangle(centerX, 0, panelWidth, 100, 0x000A22, 0.85)
      .setOrigin(0.5, 0).setStrokeStyle(2, 0x3366AA, 0.8);
  mainContainer.add(panelBg);
  this.elements.mainPanelBg = panelBg; // Store reference

  try {
      // Player Score Display Box
      currentY += panelPadding; // Top padding
      const scoreBoxHeight = 70;
      const scoreBoxY = currentY + scoreBoxHeight / 2;
      const scoreBoxWidth = Math.min(400, panelWidth * 0.8);

      const scoreBoxGlow = sceneContext.add.rectangle(centerX, scoreBoxY, scoreBoxWidth + 10, scoreBoxHeight + 10, 0x00AAFF, 0.2).setDepth(DEPTHS.PANEL_SHADOW);
      const scoreBox = sceneContext.add.rectangle(centerX, scoreBoxY, scoreBoxWidth, scoreBoxHeight, 0x001844, 0.9).setStrokeStyle(2, 0x00AAFF, 1.0).setDepth(DEPTHS.PANEL_BACKGROUND + 1);
      mainContainer.add([scoreBoxGlow, scoreBox]); // Add to main container

      const scoreText = this.createThemedText(centerX, scoreBoxY - 10, `YOUR SCORE: ${this.score.toLocaleString()}`, 'sizeXl', '#00FFFF').setDepth(DEPTHS.TEXT);
      const rankString = (this.playerRank !== null && this.totalPlayers > 0)
                         ? `GLOBAL RANK: #${this.playerRank} / ${this.totalPlayers}`
                         : `GLOBAL RANK: #---`;
      const rankText = this.createThemedText(centerX, scoreBoxY + 18, rankString, 'sizeMd', '#FFFF66').setDepth(DEPTHS.TEXT);
      mainContainer.add([scoreText, rankText]);
      this.animateGlow(scoreBoxGlow);
      currentY += scoreBoxHeight + sectionGap; // Update Y below score box

      // Leaderboard Table
      const tableWidth = panelWidth * 0.9;
      const listContainer = sceneContext.add.container(centerX, currentY); // Position container
      mainContainer.add(listContainer);

      const tableHeight = this.createAlignedScoreList(listContainer, 0, this.topIndividualScores, tableWidth, 10, true); // Pass container
      currentY += tableHeight; // Update Y based on table height

      // Add Rank Info Text if player's score wasn't in top 10 BUT rank was calculated
      let rankInfoHeight = 0;
      if (!this.playerScoreOutsideTopTen && this.playerScoreEntry && this.playerRank) {
           currentY += 5; // Small gap
           const rankInfoStyle = { fontSize: '14px', color: '#AAAAAA', fontStyle: 'italic' };
           const rankInfoText = sceneContext.add.text(centerX, currentY + 10, `Your Rank: #${this.playerRank} / ${this.totalPlayers}`, rankInfoStyle).setOrigin(0.5);
           mainContainer.add(rankInfoText);
           rankInfoHeight = 25; // Approximate height + padding for this text
           currentY += rankInfoHeight;
      }
      // Add padding below the table or the rank info text
      currentY += tableBottomPadding + sectionGap; // Use the extra padding here


      // Return Button
      const buttonY = currentY; // Position below table/rank info + gaps
      this.createReturnButton(sceneContext, buttonY); // Adds button to mainContainer
      currentY += 60 + panelPadding; // Button height + final padding

      // Finalize Panel Size and Position
      const finalPanelHeight = currentY;
      panelBg.setSize(panelWidth, finalPanelHeight);
      panelBg.setY(0); // Set Y relative to mainContainer

      // Position the main container correctly below the scene header
      mainContainer.setPosition(0, contentStartY);

  } catch (error) {
      console.error(`${logPrefix} Error creating layout:`, error);
      if (mainContainer) {
          mainContainer.removeAll(true);
          mainContainer.add(this.createThemedText(centerX, 50, "Error displaying results.", 'sizeLg', MenuUIUtils.COLORS.danger));
          this.createReturnButton(sceneContext, 150); // Add fallback button
      }
  }
}

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createMPLayout method with this:
createMPLayout(sceneContext, contentStartY, maxHeight) {
  const logPrefix = "[GameOverScene-MP]";
  console.log(`${logPrefix} Creating Multiplayer Layout Structure v9`);

  // --- Guards ---
  if (!sceneContext || !sceneContext.elements.leaderboardContainer || !sceneContext.add || !sceneContext.cameras?.main || !sceneContext.scene?.isActive) {
      console.error(`${logPrefix} Essential components missing! Cannot create layout.`);
      try { this.fallbackGameOverDisplay("UI Error: Cannot display results."); } catch (fallbackError) { console.error("Error displaying fallback message:", fallbackError); }
      return;
  }
  const mainContainer = sceneContext.elements.leaderboardContainer;
  mainContainer.removeAll(true);
  mainContainer.setPosition(0, 0); // Position main container at scene origin (contentStartY applied later if needed, but maybe not necessary)
  // --- End Guards ---

  const width = sceneContext.cameras.main.width;
  const centerX = width / 2;
  const panelWidth = Math.min(900, width * 0.9);
  const panelPadding = 20;
  const sectionGap = 30; // Standard gap
  const achievementGap = 45; // ** INCREASED GAP before achievement panel **
  let accumulatedHeight = 0; // Track height relative to mainContainer's Y=0

  // --- Main Panel Background (Sized at the end) ---
  const mainPanelBg = sceneContext.add.rectangle(centerX, 0, panelWidth, 100, 0x0A0A1A, 0.9)
      .setOrigin(0.5, 0).setStrokeStyle(2, MenuUIUtils.COLORS.secondary, 0.6);
  mainContainer.add(mainPanelBg);
  this.elements.mainPanelBg = mainPanelBg;

  try {
      // --- 1. Current Game Results Section ---
      accumulatedHeight += panelPadding; // Top padding
      const currentHeaderY = accumulatedHeight + 15;
      const currentHeader = this.createThemedText(centerX, currentHeaderY, "CURRENT GAME RESULTS", 'sizeLg', MenuUIUtils.COLORS.primary);
      if (currentHeader) mainContainer.add(currentHeader);
      accumulatedHeight += 45;

      const currentTableWidth = panelWidth * 0.9;
      const currentListContainer = sceneContext.add.container(centerX, accumulatedHeight);
      mainContainer.add(currentListContainer);
      const currentGameHeight = this.createAlignedScoreList(currentListContainer, 0, this.playerScores, currentTableWidth, 6, false);
      accumulatedHeight += currentGameHeight + sectionGap;
      console.log(`${logPrefix} Current game section height: ${currentGameHeight}`);

      // --- 2. Tabs & Leaderboard Section ---
      const tabsY = accumulatedHeight + 10;
      const tabContainer = sceneContext.add.container(centerX, tabsY);
      mainContainer.add(tabContainer);

      const tabWidth = Math.min(200, (panelWidth * 0.8) / 2);
      const soloTabBtn = this.createTabButton(sceneContext, tabContainer, -tabWidth / 2 - 5, 20, "All-Time Solo", "solo");
      const teamTabBtn = this.createTabButton(sceneContext, tabContainer, tabWidth / 2 + 5, 20, "All-Time Team", "team");
      let tabsExist = soloTabBtn && teamTabBtn;
      let tabsVisualHeight = 40; // Height of the buttons themselves
      let leaderboardSectionHeight = 0; // Height occupied by the leaderboard below tabs

      if (!tabsExist) {
          console.error(`${logPrefix} Failed to create tab buttons!`);
          accumulatedHeight += sectionGap; // Only add gap
      } else {
          this.elements.soloTabBtn = soloTabBtn;
          this.elements.teamTabBtn = teamTabBtn;
          accumulatedHeight += tabsVisualHeight + sectionGap; // Add tabs height + gap

          // Leaderboard Display Area
          let leaderboardDisplayY = accumulatedHeight;
          const leaderboardDisplayContainer = sceneContext.add.container(centerX, leaderboardDisplayY);
          mainContainer.add(leaderboardDisplayContainer);
          this.elements.soloLeaderboardArea = sceneContext.add.container(0, 0).setVisible(true);
          this.elements.teamLeaderboardArea = sceneContext.add.container(0, 0).setVisible(false);
          leaderboardDisplayContainer.add([this.elements.soloLeaderboardArea, this.elements.teamLeaderboardArea]);

          const leaderboardTableWidth = panelWidth * 0.9;
          const soloHeight = this.createAlignedScoreList(this.elements.soloLeaderboardArea, 0, this.topIndividualScores, leaderboardTableWidth, 8, false);
          const teamHeight = this.createAlignedTeamScoreList(this.elements.teamLeaderboardArea, 0, this.topTeamScores, leaderboardTableWidth, 8);
          leaderboardSectionHeight = Math.max(soloHeight, teamHeight, 50); // Actual height of the list content
          accumulatedHeight += leaderboardSectionHeight + achievementGap; // Use specific gap for achievement
          console.log(`${logPrefix} Leaderboard areas created, max height: ${leaderboardSectionHeight}`);

          const setActiveTab = (activeKey) => { /* ... (tab switching logic remains the same) ... */
               if (this.elements.soloLeaderboardArea) this.elements.soloLeaderboardArea.setVisible(activeKey === 'solo');
               if (this.elements.teamLeaderboardArea) this.elements.teamLeaderboardArea.setVisible(activeKey === 'team');
               if (soloTabBtn) soloTabBtn.setActive(activeKey === 'solo');
               if (teamTabBtn) teamTabBtn.setActive(activeKey === 'team');
          };
          soloTabBtn.on('pointerdown', () => setActiveTab('solo'));
          teamTabBtn.on('pointerdown', () => setActiveTab('team'));
          setActiveTab('solo');
      }

      // --- 4. AI Achievement Panel ---
      let achievementY = accumulatedHeight; // Position below previous content (now includes achievementGap)
      if (this.elements.achievementContainer) {
           if (!mainContainer.list.includes(this.elements.achievementContainer)) {
               mainContainer.add(this.elements.achievementContainer);
           }
          this.createAchievementPanel(sceneContext, 0, 120);
          const achievementActualHeight = this.updateAchievementPanel();
          // Position the container CENTERED horizontally, and vertically based on accumulated height and its own height
          this.elements.achievementContainer.setPosition(centerX, achievementY + achievementActualHeight / 2);
          this.elements.achievementContainer.setVisible(true);
          accumulatedHeight += achievementActualHeight + sectionGap; // Add its height + standard gap
          console.log(`${logPrefix} Achievement panel positioned at Y=${achievementY + achievementActualHeight / 2}, height: ${achievementActualHeight}`);
      } else {
          console.warn(`${logPrefix} Achievement container missing.`);
          accumulatedHeight += sectionGap;
      }

      // --- 5. Return Button ---
      let buttonY = accumulatedHeight + 30; // Position below achievement + extra gap
      this.createReturnButton(sceneContext, buttonY); // Adds button relative to mainContainer
      accumulatedHeight += 60 + panelPadding; // Button height + final padding
      console.log(`${logPrefix} Return button created at Y=${buttonY}`);

      // --- Finalize Main Panel Height & Position ---
      const finalPanelHeight = accumulatedHeight;
      if (this.elements.mainPanelBg && this.elements.mainPanelBg.active) {
          this.elements.mainPanelBg.setSize(panelWidth, finalPanelHeight);
          this.elements.mainPanelBg.setPosition(centerX, 0); // BG Y is relative to mainContainer start (Y=0)
          console.log(`${logPrefix} Final panel height set: ${finalPanelHeight}`);
      } else {
          console.warn(`${logPrefix} Main panel background missing/inactive.`);
      }

      // Set final position of the main container (leaderboardContainer) relative to the SCENE
      mainContainer.setPosition(0, contentStartY);

  } catch (layoutError) {
      console.error(`${logPrefix} Error during MP layout creation:`, layoutError);
      if (mainContainer && mainContainer.active) {
          mainContainer.removeAll(true);
          mainContainer.add(this.createThemedText(centerX, 50, "Error displaying results.", 'sizeLg', MenuUIUtils.COLORS.danger));
          this.createReturnButton(sceneContext, 150);
      }
  }
}
// Replace the entire createMPLayout method with this:


    // Helper to create Tab Buttons for MP Layout
    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createTabButton method with this:
// Helper to create Tab Buttons for MP Layout
createTabButton(sceneContext, container, x, y, label, key) { // Accept sceneContext
  if (!container || !sceneContext || !sceneContext.add) { // Check sceneContext.add
       console.error("Invalid container or sceneContext passed to createTabButton for label:", label);
       return { setActive: () => {}, on: () => {}, setInteractive: () => {}, disableInteractive: () => {}, getData: () => null, setData: () => {}, width: 0, height: 0 };
  }

  // --- Use sceneContext for button creation ---
   const btn = MenuUIUtils.createStyledButton(sceneContext, x, y, label, () => {}, {
       bgColor: MenuUIUtils.COLORS.bgLight,
       textColor: MenuUIUtils.COLORS.textMedium,
       hoverColor: MenuUIUtils.COLORS.bgMedium,
       width: Math.min(200, (sceneContext.cameras.main.width * 0.8) / 2 - 10), // Use sceneContext camera
       height: 40,
       fontSize: '16px'
   });
   // --- End Use sceneContext ---

   if (!btn) {
        console.error(`Failed to create tab button for label "${label}"`);
        return { setActive: () => {}, on: () => {}, setInteractive: () => {}, disableInteractive: () => {}, getData: () => null, setData: () => {}, width: 0, height: 0 };
   }

   btn.setActive = (isActive) => {
       const bg = btn.getData('rectGraphic');
       const text = btn.getData('textObject');
       if (!bg || !text) {
           console.warn(`Cannot set active state for "${label}": button graphics or text missing.`);
           return;
       }

       if (isActive) {
           bg.clear().fillStyle(MenuUIUtils.COLORS.secondary, 1).fillRoundedRect(-btn.width/2, -btn.height/2, btn.width, btn.height, MenuUIUtils.SPACING.xs);
           text.setColor(Phaser.Display.Color.ValueToColor(MenuUIUtils.COLORS.bgDark).rgba);
           if(btn.input) btn.disableInteractive();
       } else {
           bg.clear().fillStyle(MenuUIUtils.COLORS.bgLight, 1).fillRoundedRect(-btn.width/2, -btn.height/2, btn.width, btn.height, MenuUIUtils.SPACING.xs);
           text.setColor(Phaser.Display.Color.ValueToColor(MenuUIUtils.COLORS.textMedium).rgba);
           if(btn.input) btn.setInteractive({ useHandCursor: true });
       }
   };

   container.add(btn); // Add the created button to the passed container
   return btn;
}

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createReturnButton method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createReturnButton method with this:
createReturnButton(sceneContext, yPos) { // Accept sceneContext
  if (!sceneContext || !sceneContext.add || !sceneContext.cameras?.main) {
      console.error("Cannot create return button: Invalid sceneContext.");
      return;
  }
  const centerX = sceneContext.cameras.main.width / 2;
  const btnWidth = 280;
  const btnHeight = 60;

  // Use 'this' for the callback context, but sceneContext for creation
  const returnBtn = this.createStyledButton(
      centerX, yPos,
      "RETURN TO MENU",
      () => this.returnToMainMenu(), // Callback uses 'this' instance
      MenuUIUtils.COLORS.success,
      btnWidth, btnHeight, 'sizeLg'
  );

  if (returnBtn) {
      // --- Add to the main leaderboardContainer ---
      if (sceneContext.elements.leaderboardContainer) {
           sceneContext.elements.leaderboardContainer.add(returnBtn);
      } else {
          console.warn("leaderboardContainer missing, adding return button directly to scene.");
          sceneContext.add.existing(returnBtn);
      }
      // --- End Add to Container ---

      this.elements.returnBtn = returnBtn; // Store reference in 'this.elements'

       // Animate button appearance
       returnBtn.setAlpha(0).setScale(0.8);
       sceneContext.tweens?.add({ targets: returnBtn, alpha: 1, scale: 1, duration: 300, delay: 200, ease: 'Back.easeOut' });
  } else {
      console.error("Failed to create Return to Menu button.");
  }
}

    returnToMainMenu() {
        console.log("[GameOverScene] Returning to Main Menu...");
        // Stop potential score update timer
        if (this.updateTimer) { this.updateTimer.remove(); this.updateTimer = null; }

        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
        
        // CRITICAL FIX: Use feature flag routing instead of hard-coded scene names
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        
        let targetScene;
        if (useUniversalLanding) {
            targetScene = 'MainMenuSceneUniversal';
        } else if (isEffectivelyLoggedIn) {
            targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
        } else {
            // CRITICAL FIX: Always use DOM or Universal scenes  
            const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
            targetScene = useUniversalLanding ? "MainMenuSceneUniversal" : "GuestMenuSceneDOM";
        }
        
        console.log(`MultiplayerGameOverScene: Returning to menu - ${targetScene}`);
        sceneManager.switchToScene(targetScene);
    }

    // --- Score Fetching (Keep existing implementations) ---
    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire fetchTopIndividualScores method with this:
async fetchTopIndividualScores(limit = 10, gameType = "Shortcut", filterSoloOnly = false) {
  const logPrefix = "[GameOverScene FetchIndividual]";
  try {
      console.log(`${logPrefix} START - Game: ${gameType}, Limit: ${limit}, SoloOnly: ${filterSoloOnly}`);

      let dbScoresData = [];
      let userProfiles = {};
      this.topIndividualScores = [];
      this.playerRank = null;
      this.totalPlayers = 0;
      this.playerScoreEntry = null;
      this.playerScoreOutsideTopTen = false;

      const user = auth.currentUser;
      const specialAccess = hasSpecialAccess && hasSpecialAccess();
      let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null);
      let currentPlayerName = this.playerDisplayName;

      let currentGameScoreData = null;
      if (typeof this.score === 'number' && this.score >= 0 && this.isSinglePlayer) {
          currentGameScoreData = {
              name: currentPlayerName, score: this.score, date: new Date(),
              userId: currentUserId, isCurrentGame: true, rank: 0, verified: !!user
          };
      }

      if (!db || typeof db.collection !== 'function') throw new Error("Database connection error");

      const gameTypesToQuery = (gameType === "Shortcut" || gameType === "MultiplayerShortcut" || gameType?.toLowerCase() === "slide wars")
          ? ["Shortcut", "MultiplayerShortcut", "slide wars", "Slide Wars"] : [gameType];
      console.log(`${logPrefix} Querying Firestore for game types: ${gameTypesToQuery.join(', ')}`);

      // --- Query 1: Get Top Scores for Display ---
      let topScoresQuery = db.collection('scores').where('game', 'in', gameTypesToQuery).orderBy('score', 'desc').limit(limit);
      if (filterSoloOnly) topScoresQuery = topScoresQuery.where('teamName', '==', null);
      const topScoresSnapshot = await topScoresQuery.get();
      console.log(`${logPrefix} Firestore Top Scores query returned ${topScoresSnapshot.size} scores.`);
      topScoresSnapshot.forEach(doc => { dbScoresData.push({ id: doc.id, ...doc.data() }); });

      // --- Query 2: Player Rank ---
      if (currentUserId && typeof this.score === 'number' && this.score >= 0) {
          console.log(`${logPrefix} Calculating player rank for score ${this.score}...`);
          try {
              let rankQuery = db.collection('scores').where('game', 'in', gameTypesToQuery).where('score', '>', this.score);
              // ** Crucial: Apply the same filter as the leaderboard display for accurate rank context **
              if (filterSoloOnly) {
                  rankQuery = rankQuery.where('teamName', '==', null);
              }
              const higherScoresSnapshot = await rankQuery.get();
              this.playerRank = higherScoresSnapshot.size + 1;
              console.log(`${logPrefix} Calculated player rank: #${this.playerRank}`);
              if (currentGameScoreData) currentGameScoreData.rank = this.playerRank;
          } catch (rankError) { console.error(`${logPrefix} Error calculating player rank:`, rankError); this.playerRank = null; }
      } else { console.log(`${logPrefix} Skipping rank calculation.`); this.playerRank = null; }

      // --- Query 3: Total Player Count (Matching Rank/Filter Scope) ---
      try {
          let countQuery = db.collection('scores').where('game', 'in', gameTypesToQuery);
          // Apply same filter as rank query
          if (filterSoloOnly) {
              countQuery = countQuery.where('teamName', '==', null);
          }
          const countSnapshot = await countQuery.get();
          this.totalPlayers = countSnapshot.size; // Count scores matching the filter
          console.log(`${logPrefix} Total players found matching filter: ${this.totalPlayers}`);
      } catch(countError) { console.error(`${logPrefix} Error fetching total player count:`, countError); this.totalPlayers = dbScoresData.length; }


      // --- Fetch Profiles ---
      const userIdsToFetch = new Set();
      dbScoresData.forEach(data => { if (data && data.userUID && data.userUID !== currentUserId) userIdsToFetch.add(data.userUID); });
      if (currentUserId && !userProfiles[currentUserId]) userIdsToFetch.add(currentUserId);

      if (userIdsToFetch.size > 0) {
          console.log(`${logPrefix} Fetching ${userIdsToFetch.size} profiles...`);
          const userIdArray = Array.from(userIdsToFetch);
          const batchSize = 10;
          for (let i = 0; i < userIdArray.length; i += batchSize) {
              const batch = userIdArray.slice(i, i + batchSize);
              if (batch.length > 0) {
                  try {
                      const profilesSnapshot = await db.collection('userProfiles').where(firebase.firestore.FieldPath.documentId(), 'in', batch).get();
                      profilesSnapshot.forEach(doc => { if (doc.exists && doc.data().displayName) userProfiles[doc.id] = { displayName: doc.data().displayName }; });
                  } catch (profileFetchError) { console.warn(`${logPrefix} Error fetching profile batch:`, profileFetchError); }
              }
          }
           if (currentUserId && userProfiles[currentUserId]?.displayName) {
              currentPlayerName = userProfiles[currentUserId].displayName;
              this.playerDisplayName = currentPlayerName;
              if(currentGameScoreData) currentGameScoreData.name = currentPlayerName;
           }
          console.log(`${logPrefix} Fetched ${Object.keys(userProfiles).length} profiles.`);
      }

      // --- Format Scores for Display & Rank ---
      let processedScores = [];
      let currentRank = 1; let scoresAtRank = 1;
      for (let i = 0; i < dbScoresData.length; i++) {
           const data = dbScoresData[i];
           if (data && typeof data.score === 'number') {
               if (i > 0 && data.score < dbScoresData[i-1].score) { currentRank += scoresAtRank; scoresAtRank = 1; }
               else if (i > 0) { scoresAtRank++; }

               const userId = data.userUID || 'unknown';
               // --- Final Name Resolution Logic ---
               let displayName = userProfiles[userId]?.displayName || // 1. Profile Name
                                 (userId === currentUserId ? currentPlayerName : null) || // 2. Resolved Current Player Name
                                 (data.player && !String(data.player).includes('@') ? data.player : null) || // 3. Saved Name (not email)
                                 "Player"; // 4. Fallback
               // --- End Name Resolution ---

              const isScoreFromThisSession = (userId === currentUserId) && (data.score === this.score);

              processedScores.push({
                  name: displayName, score: data.score, date: data.createdAt?.toDate() || new Date(),
                  userId: userId, highlight: isScoreFromThisSession, rank: currentRank, verified: data.verified === true
              });
          }
      }

      // Determine if current player's SP score needs the extra row
      const isInTopList = processedScores.some(s => s.highlight);
      if (this.isSinglePlayer && currentGameScoreData && !isInTopList && this.playerRank !== null) {
          if (this.playerRank > limit) {
               this.playerScoreOutsideTopTen = true;
               this.playerScoreEntry = currentGameScoreData;
               console.log(`${logPrefix} Current SP score outside top ${limit}, adding extra row.`);
          } else {
               this.playerScoreEntry = currentGameScoreData;
               this.playerScoreOutsideTopTen = false;
               console.log(`${logPrefix} Current SP score rank (#${this.playerRank}) within top ${limit}, but not in displayed list.`);
          }
      } else {
           this.playerScoreOutsideTopTen = false;
           this.playerScoreEntry = null;
      }

      this.topIndividualScores = processedScores; // Assign the formatted top scores
      if (filterSoloOnly) this.topSoloScores = processedScores; // Keep this specific list if needed elsewhere

      console.log(`${logPrefix} Fetch completed: ${this.topIndividualScores.length} scores for display, player rank: ${this.playerRank}, total players: ${this.totalPlayers}`);

  } catch (error) {
      console.error(`${logPrefix} CRITICAL ERROR:`, error);
      // Fallback remains the same
      this.topIndividualScores = [{ rank: 1, name: "Error", score: 0, date: new Date(), userId:"error", verified: false, highlight: false }];
      this.playerRank = null; this.totalPlayers = 0; this.playerScoreOutsideTopTen = false; this.playerScoreEntry = null;
  }
}

    async fetchTopTeamScores(limit = 10) {
         // ... (Keep the robust implementation from the prompt) ...
         // Ensure it sets this.topTeamScores
          try {
            console.log(`[GameOverScene] Fetching top team scores - Start`);

            let currentTeamScore = null;
            // Only calculate current team score if in a multiplayer context with multiple players
            if (!this.isSinglePlayer && this.playerScores && this.playerScores.length > 0) {
                 const teamName = this.teamName || `Team_${this.gameCode ? this.gameCode.substring(0, 3) : 'MP'}`;
                 const totalScore = this.playerScores.reduce((sum, player) => sum + (player.score || 0), 0);
                 if (totalScore >= 0) { // Allow zero score teams
                     currentTeamScore = {
                         rank: 0, teamName: teamName, totalScore: totalScore,
                         score: totalScore, // Compatibility field
                         playerCount: this.playerScores.length,
                         date: new Date(), isCurrentGame: true, gameCode: this.gameCode
                     };
                      console.log(`[GameOverScene] Current team score calculated: ${totalScore} for ${teamName}`);
                 }
            }

            // Fetch team scores from database
            const snapshot = await db.collection('teamScores')
                .orderBy('totalScore', 'desc')
                .limit(limit + 5) // Fetch a bit more to handle potential duplicates/insertions
                .get();

            let dbTeamScores = [];
            if (!snapshot.empty) {
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    // Basic validation
                    if(data && typeof data.totalScore === 'number') {
                         dbTeamScores.push({
                              rank: 0, // Will assign later
                              teamName: data.teamName || 'Team',
                              score: data.totalScore, totalScore: data.totalScore,
                              playerCount: data.playerCount || 0,
                              date: data.timestamp?.toDate() || new Date(),
                              gameCode: data.gameCode
                         });
                    }
                });
            }
             console.log(`[GameOverScene] Fetched ${dbTeamScores.length} team scores from DB.`);

             // Combine and sort
             let allTeamScores = [...dbTeamScores];
             let teamScoreInList = false;

             if (currentTeamScore) {
                 // Check if current score *roughly* matches an existing recent score (prevent duplicates)
                 const duplicateIndex = allTeamScores.findIndex(dbScore =>
                      dbScore.teamName === currentTeamScore.teamName &&
                      dbScore.score === currentTeamScore.score &&
                      Math.abs(dbScore.date.getTime() - currentTeamScore.date.getTime()) < 5 * 60 * 1000 // Within 5 mins
                 );

                 if (duplicateIndex !== -1) {
                     console.log("Found potential duplicate team score, marking existing as current.");
                     allTeamScores[duplicateIndex].isCurrentGame = true; // Mark existing as current
                     allTeamScores[duplicateIndex].gameCode = this.gameCode; // Update game code if needed
                     teamScoreInList = true;
                 } else {
                     console.log("Adding current team score to list.");
                     allTeamScores.push(currentTeamScore);
                 }
             }

             // Sort final list and assign ranks
             allTeamScores.sort((a, b) => b.score - a.score);
             allTeamScores.forEach((s, index) => { s.rank = index + 1; });

            // Limit to desired size and assign to class property
            this.topTeamScores = allTeamScores.slice(0, limit);
            console.log(`[GameOverScene] Final team scores count: ${this.topTeamScores.length}`);

        } catch (error) {
            console.error(`Error fetching top team scores:`, error);
            this.topTeamScores = [{ rank: 1, teamName: "Error", score: 0, date: new Date() }]; // Fallback
        }
    }

    async generateAiContent() {
        // ... (Keep the robust implementation from the prompt) ...
        // Ensure it sets this.gameOverTitle and this.playerAchievement
         console.log("Starting AI content generation...");
         let playerRank = 1; let totalPlayers = 1;
         let playerDataForAI = { player: this.playerDisplayName, score: this.score };

         try {
             if (!this.playerScores || this.playerScores.length === 0) {
                  console.warn("AI Content: Using single player score as playerScores is empty/invalid.");
                 this.playerScores = [{ userId: this.userId, name: this.playerDisplayName, score: this.score, rank: 1 }];
             }

             if(this.playerScores.length > 0) {
                 const playerIndex = this.playerScores.findIndex(p => p.userId === this.userId);
                 totalPlayers = this.playerScores.length;
                 if (playerIndex !== -1) {
                      playerRank = this.playerScores[playerIndex].rank || (playerIndex + 1); // Use stored rank first
                      playerDataForAI = this.playerScores[playerIndex];
                 } else {
                      // Player not found in current game scores (maybe joined late/error?)
                      playerRank = totalPlayers + 1; // Rank last
                      playerDataForAI = { player: this.playerDisplayName, score: this.score }; // Use scene score
                      console.warn(`Current player ${this.userId} not found in playerScores for AI generation.`);
                 }
             } else {
                  // Fallback if playerScores is still empty
                  playerRank = 1; totalPlayers = 1;
                  playerDataForAI = { player: this.playerDisplayName, score: this.score };
             }


              console.log("AI Content Generation Input:", { rank: playerRank, totalPlayers, score: playerDataForAI.score });

             try {
                 if (!this.claudeApi) this.claudeApi = new claudeAPIService();

                 // Generate Title and Achievement concurrently
                 const [titleResult, achievementResult] = await Promise.allSettled([
                     this.claudeApi.generateGameOverTitle(this.gameData, this.playerScores, this.userId),
                     this.claudeApi.generatePersonalizedAchievement(playerDataForAI, playerRank, totalPlayers)
                 ]);

                 // Process Title
                 if (titleResult.status === 'fulfilled' && titleResult.value) {
                     this.gameOverTitle = titleResult.value;
                 } else {
                     console.warn("Failed to generate AI title:", titleResult.reason);
                     this.gameOverTitle = this.getFallbackTitle(playerRank); // Use fallback
                 }
                  console.log("Generated Game Over Title:", this.gameOverTitle);

                 // Process Achievement
                 if (achievementResult.status === 'fulfilled' && achievementResult.value?.name && achievementResult.value?.description) {
                     this.playerAchievement = achievementResult.value;
                 } else {
                      console.warn("Failed to generate AI achievement or invalid format:", achievementResult.reason || achievementResult.value);
                     this.playerAchievement = this.getFallbackAchievement(playerRank, totalPlayers); // Use fallback
                 }
                  console.log("Generated Player Achievement:", this.playerAchievement);

             } catch (apiError) { // Catch errors from Promise.all or Claude service itself
                 console.error("Claude API calls failed:", apiError);
                 this.gameOverTitle = this.getFallbackTitle(playerRank);
                 this.playerAchievement = this.getFallbackAchievement(playerRank, totalPlayers);
             }

         } catch (error) {
             console.error("Error preparing data for AI content:", error);
             this.gameOverTitle = "GAME OVER";
             this.playerAchievement = this.getFallbackAchievement(1, 1);
         }

         // Update UI elements if they exist already (async nature)
         if (this.elements.headerText) this.elements.headerText.setText(this.gameOverTitle);
         if (this.elements.achievementContainer) this.updateAchievementPanel(); // Update panel content

         console.log("AI content generation finished.");
    }

    getFallbackTitle(rank) {
         if (rank === 1) return "VICTORY!";
         if (rank <= 3) return "TOP PERFORMER";
         return "ROUND COMPLETE";
    }


    // --- UI Helpers ---

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createAchievementPanel method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createAchievementPanel method with this:
createAchievementPanel(sceneContext, relativeY, initialHeight = 120) {
  // --- Use sceneContext ---
  if (!sceneContext || !this.elements.achievementContainer || !sceneContext.add) { // Check this.elements...
      console.warn("Achievement container or sceneContext missing for creation");
      return;
  }
  const container = this.elements.achievementContainer; // Use the pre-created container
  container.removeAll(true); // Clear previous content

  const width = sceneContext.cameras.main.width;
  const panelWidth = Math.min(500, width * 0.8);
  const padding = 15;

  // --- Position elements relative to container's (0,0) ---

  // Panel background - Centered at (0,0) within the container
  this.elements.achievementBg = sceneContext.add.rectangle(0, 0, panelWidth, initialHeight, 0x001842, 0.85)
      .setOrigin(0.5, 0.5) // Center origin
      .setStrokeStyle(2, 0x3366CC, 0.8)
      .setDepth(DEPTHS.ACHIEVEMENT_PANEL - 1); // Ensure it's behind text
  container.add(this.elements.achievementBg);

  // Title - Centered horizontally, offset vertically from container center
  const titleYOffset = -initialHeight / 2 + padding + 12; // Calculate Y relative to center
  this.elements.achievementTitle = this.createThemedText(0, titleYOffset, this.playerAchievement.name, 'sizeLg', '#66CCFF')
      .setOrigin(0.5) // Center horizontally
      .setDepth(DEPTHS.ACHIEVEMENT_TEXT);
  container.add(this.elements.achievementTitle);

  // Description - Centered horizontally, positioned below title
  const descriptionYOffset = titleYOffset + this.elements.achievementTitle.height / 2 + padding;
  this.elements.achievementDescription = sceneContext.add.text(0, descriptionYOffset, this.playerAchievement.description, {
          fontFamily: 'monospace', fontSize: '16px', color: '#FFFFFF', align: 'center',
          wordWrap: { width: panelWidth - (padding * 2) }, lineSpacing: 4
      })
      .setOrigin(0.5, 0) // Origin top-center for wrapping
      .setDepth(DEPTHS.ACHIEVEMENT_TEXT);
  container.add(this.elements.achievementDescription);

  // Glow Effect - Centered at (0,0)
  this.elements.achievementGlow = sceneContext.add.rectangle(0, 0, panelWidth + 15, initialHeight + 15, 0x0044AA, 0.25)
      .setOrigin(0.5, 0.5) // Center origin
      .setDepth(DEPTHS.ACHIEVEMENT_GLOW);
  container.add(this.elements.achievementGlow);
  this.animateGlow(this.elements.achievementGlow);

  // Set visibility based on game mode AFTER adding elements
  container.setVisible(!this.isSinglePlayer);
  // --- End Element Positioning ---
}

    // src/scenes/MultiplayerGameOverScene.js

// Replace the entire updateAchievementPanel method with this:
updateAchievementPanel() {
  if (!this.elements.achievementContainer || !this.elements.achievementTitle || !this.elements.achievementDescription || !this.elements.achievementBg) {
      console.warn("Cannot update achievement panel: elements missing.");
      return 120; // Return default height
  }
  const container = this.elements.achievementContainer;

  // Stop previous pulse if running
  if (this.achievementPulseEffect) { this.achievementPulseEffect.stop(); this.elements.achievementTitle.setScale(1); }

  const panelWidth = this.elements.achievementBg.width; // Get width from existing BG
  const padding = 15;

  // Update Text Content
  this.elements.achievementTitle.setText(this.playerAchievement.name || "Achievement");
  this.elements.achievementDescription.setText(this.playerAchievement.description || "Well done!");

  // Calculate Required Height based on new text bounds
  const titleHeight = this.elements.achievementTitle.getBounds().height;
  const descHeight = this.elements.achievementDescription.getBounds().height;
  const requiredHeight = (padding * 3) + titleHeight + descHeight; // top_pad + title + pad + desc + bottom_pad
  const finalHeight = Math.max(100, requiredHeight); // Ensure min height

  // --- Reposition elements relative to container center (0,0) ---
  const titleYOffset = -finalHeight / 2 + padding + titleHeight / 2;
  const descriptionYOffset = titleYOffset + titleHeight / 2 + padding;
  this.elements.achievementTitle.setPosition(0, titleYOffset);
  this.elements.achievementDescription.setPosition(0, descriptionYOffset);
  // --- End Reposition ---

  // Animate Panel Resize
  const bgTargetY = 0; // Background remains centered at container's (0,0)
  this.tweens?.add({ targets: this.elements.achievementBg, displayHeight: finalHeight, y: bgTargetY, duration: 300, ease: 'Sine.easeInOut' });
  if(this.elements.achievementGlow) {
      this.tweens?.add({ targets: this.elements.achievementGlow, displayHeight: finalHeight + 15, y: bgTargetY, duration: 300, ease: 'Sine.easeInOut' });
  }

   // Determine Color and Animate Title (remains the same)
   let color = MenuUIUtils.COLORS.secondary;
   const nameUpper = (this.playerAchievement.name || "").toUpperCase();
   if (nameUpper.includes("CHAMPION") || nameUpper.includes("VICTORY") || nameUpper.includes("MASTER")) color = MenuUIUtils.COLORS.primary;
   else if (nameUpper.includes("EXPERT") || nameUpper.includes("TOP")) color = MenuUIUtils.COLORS.warning;
   else if (nameUpper.includes("DODGER") || nameUpper.includes("SURVIVOR")) color = MenuUIUtils.COLORS.accent1;

   this.elements.achievementTitle.setColor(Phaser.Display.Color.ValueToColor(color).rgba);
   this.elements.achievementTitle.setShadow(0, 0, Phaser.Display.Color.ValueToColor(color).rgba, 10, true, true);
   this.achievementPulseEffect = this.tweens?.add({ targets: this.elements.achievementTitle, scale: 1.05, duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });

  return finalHeight; // Return calculated height
}

 
     // src/scenes/MultiplayerGameOverScene.js

// Replace the entire createAlignedScoreList method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createAlignedScoreList method with this:
createAlignedScoreList(container, startY, scores, tableWidth, maxRows, isSinglePlayerFormat = false) {
  // --- Guards ---
  if (!container || !container.scene || !container.scene.add || !container.active) {
      console.error("createAlignedScoreList: Invalid container or scene context."); return 0;
  }
  const scene = container.scene; container.removeAll(true);
  // --- End Guards ---

  const rowHeight = 38; const headerHeight = 30; const cellPadding = 15;
  let currentY = startY;

  // --- Column Widths & X Positions ---
  const rankWidth = tableWidth * 0.12;
  const nameWidth = tableWidth * (isSinglePlayerFormat ? 0.38 : 0.53);
  const dateWidth = isSinglePlayerFormat ? tableWidth * 0.20 : 0;
  const scoreWidth = tableWidth * (isSinglePlayerFormat ? 0.30 : 0.35);

  const rankX = -tableWidth / 2 + rankWidth / 2;
  const nameX = -tableWidth / 2 + rankWidth + cellPadding;
  const dateX = isSinglePlayerFormat ? (nameX + nameWidth + cellPadding + dateWidth / 2) : -Infinity; // Center date column
  const scoreX = tableWidth / 2 - cellPadding; // Right align X edge

  // --- Table Header ---
  try {
      const headerBg = scene.add.rectangle(0, currentY + headerHeight / 2, tableWidth, headerHeight, 0x003366, 0.9).setOrigin(0.5);
      container.add(headerBg);
      const headerTextStyle = { fontSize: '14px', color: '#AAAAAA', fontStyle: 'bold' };
      container.add(scene.add.text(rankX, currentY + headerHeight / 2, "RANK", headerTextStyle).setOrigin(0.5));
      container.add(scene.add.text(nameX, currentY + headerHeight / 2, "PLAYER", headerTextStyle).setOrigin(0, 0.5)); // Left Align Header
      if (isSinglePlayerFormat) container.add(scene.add.text(dateX, currentY + headerHeight / 2, "DATE", headerTextStyle).setOrigin(0.5));
      container.add(scene.add.text(scoreX, currentY + headerHeight / 2, "SCORE", headerTextStyle).setOrigin(1, 0.5)); // Right Align Header
      currentY += headerHeight + 5;
  } catch(e) { console.error("Error creating header:", e); currentY += headerHeight + 5; }

  // --- Score Rows ---
  const displayScores = scores ? scores.slice(0, maxRows) : [];
  if (displayScores.length === 0) {
      try { container.add(scene.add.text(0, currentY + 20, "No scores available.", { fontSize: '16px', color: '#888888' }).setOrigin(0.5)); currentY += 40; }
      catch(e) { console.error("Error adding 'no scores' text:", e); }
  } else {
      displayScores.forEach((score, index) => {
          try {
              const rowY = currentY + rowHeight / 2;
              const isHighlighted = score.highlight === true;
              let bgColor = index % 2 === 0 ? 0x101528 : 0x182038; let strokeColor = 0x203050; let strokeThickness = 1;
              if (isHighlighted) { bgColor = 0x154560; strokeColor = MenuUIUtils.COLORS.secondary; strokeThickness = 1.5; }

              const rowBg = scene.add.rectangle(0, rowY, tableWidth, rowHeight - 4, bgColor, 0.9).setStrokeStyle(strokeThickness, strokeColor, 0.8).setOrigin(0.5);
              container.add(rowBg);

              // Rank (Centered)
              let rankColor = isHighlighted ? MenuUIUtils.COLORS.secondary : '#FFFFFF';
              if (index === 0 && !isHighlighted) rankColor = '#FFD700'; else if (index === 1 && !isHighlighted) rankColor = '#C0C0C0'; else if (index === 2 && !isHighlighted) rankColor = '#CD7F32';
              container.add(scene.add.text(rankX, rowY, `${score.rank || index + 1}`, { fontSize: '16px', color: rankColor, fontStyle: 'bold' }).setOrigin(0.5));

              // Player Name (Left Aligned)
              let displayName = score.name || score.player || 'Player'; const nameLengthLimit = 18; if (displayName.length > nameLengthLimit) displayName = displayName.substring(0, nameLengthLimit - 1) + '…';
              const nameTextStyle = { fontSize: '16px', color: isHighlighted ? MenuUIUtils.COLORS.secondary_light : '#FFFFFF', fontStyle: isHighlighted ? 'bold' : 'normal' };
              const nameText = scene.add.text(nameX, rowY, displayName, nameTextStyle).setOrigin(0, 0.5); // Left Align
              container.add(nameText);

              // Verified Star
              if (score.verified) {
                  const starX = nameText.x + nameText.width + 5;
                  container.add(scene.add.text(starX, rowY, '★', { fontSize: '14px', color: MenuUIUtils.COLORS.primary_light }).setOrigin(0, 0.5));
              }

              // Date (SP Only, Centered)
              if (isSinglePlayerFormat && typeof dateX === 'number') {
                  let dateStr = '-'; if (score.date instanceof Date) { try { dateStr = score.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch (e) {} }
                  container.add(scene.add.text(dateX, rowY, dateStr, { fontSize: '14px', color: '#AAAAAA' }).setOrigin(0.5));
              }

              // Score (Right Aligned)
              container.add(scene.add.text(scoreX, rowY, (score.score || 0).toLocaleString(), { fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold' }).setOrigin(1, 0.5)); // Align right

              currentY += rowHeight;
          } catch (rowError) { console.error(`Error creating score row ${index}:`, rowError); }
      });
  }
 // --- Add player's score row if outside top list (SP Mode Only) ---
 if (isSinglePlayerFormat && this.playerScoreOutsideTopTen && this.playerScoreEntry) {
     try {
         // Check positions again
         if (typeof rankX !== 'number' || typeof nameX !== 'number' || typeof scoreX !== 'number' || typeof dateX !== 'number') {
              console.error("Column positions undefined for extra player row. Skipping.");
         } else {
             currentY += 10;
             const separator = scene.add.rectangle(0, currentY, tableWidth * 0.8, 1, 0x444444, 0.7).setOrigin(0.5);
             container.add(separator);
             currentY += 15;

             const rowY = currentY + rowHeight / 2;
             const rowBg = scene.add.rectangle(0, rowY, tableWidth, rowHeight - 4, 0x154560, 0.9).setStrokeStyle(1.5, MenuUIUtils.COLORS.secondary, 0.8).setOrigin(0.5);
             container.add(rowBg);

             // Rank (Centered)
             container.add(scene.add.text(rankX, rowY, `#${this.playerScoreEntry.rank}`, { fontSize: '16px', color: MenuUIUtils.COLORS.secondary, fontStyle: 'bold' }).setOrigin(0.5));

             // Player Name + Star (Separate, Left Aligned)
             let displayName = this.playerScoreEntry.name || this.playerDisplayName || 'Player';
             if (displayName.length > 18) displayName = displayName.substring(0, 15) + '…';
             const nameText = scene.add.text(nameX, rowY, displayName, { fontSize: '16px', color: MenuUIUtils.COLORS.secondary_light, fontStyle: 'bold' }).setOrigin(0, 0.5);
             container.add(nameText);
             if (this.playerScoreEntry.verified) {
                 const starText = scene.add.text(nameText.x + nameText.width + 5, rowY, '★', { fontSize: '14px', color: MenuUIUtils.COLORS.secondary_light }).setOrigin(0, 0.5);
                 container.add(starText);
             }

             // Date (Centered)
             let dateStr = 'Now'; try { if(this.playerScoreEntry.date instanceof Date) dateStr = this.playerScoreEntry.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch(e){}
             container.add(scene.add.text(dateX, rowY, dateStr, { fontSize: '14px', color: '#AAAAAA' }).setOrigin(0.5));

             // Score (Right Aligned)
             container.add(scene.add.text(scoreX, rowY, (this.playerScoreEntry.score || 0).toLocaleString(), { fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold' }).setOrigin(1, 0.5));

             currentY += rowHeight;
         }
      } catch(extraRowError) { console.error("Error creating extra player score row:", extraRowError); }
  }
  // --- End Extra Row Logic ---

  return currentY - startY; // Return total height used
}

// Replace the entire createAlignedTeamScoreList method with this:
// src/scenes/MultiplayerGameOverScene.js

// Replace the entire createAlignedTeamScoreList method with this:
createAlignedTeamScoreList(container, startY, scores, tableWidth, maxRows) {
  // --- Guards ---
  if (!container || !container.scene || !container.scene.add || !container.active) {
      console.error("createAlignedTeamScoreList: Invalid container or scene context."); return 0;
  }
  const scene = container.scene; container.removeAll(true);
  // --- End Guards ---

  const rowHeight = 38; const headerHeight = 30; const cellPadding = 15;
  let currentY = startY;

  // --- Define Column Widths & X Positions ---
  const rankWidth = tableWidth * 0.12;
  const nameWidth = tableWidth * 0.43;
  const countWidth = tableWidth * 0.20;
  const scoreWidth = tableWidth * 0.25;

  const rankX = -tableWidth / 2 + rankWidth / 2; // Center Rank
  const nameX = -tableWidth / 2 + rankWidth + cellPadding; // Left Align Name
  const countX = nameX + nameWidth + countWidth / 2; // Center Count after Name
  const scoreX = tableWidth / 2 - cellPadding; // Right Align Score
  // --- End Definitions ---

  // --- Header ---
  try {
      const headerBg = scene.add.rectangle(0, currentY + headerHeight / 2, tableWidth, headerHeight, 0x402060, 0.9).setOrigin(0.5);
      container.add(headerBg);
      const headerTextStyle = { fontSize: '14px', color: '#AAAAAA', fontStyle: 'bold' };

      container.add(scene.add.text(rankX, currentY + headerHeight / 2, "RANK", headerTextStyle).setOrigin(0.5));
      container.add(scene.add.text(nameX, currentY + headerHeight / 2, "TEAM NAME", headerTextStyle).setOrigin(0, 0.5)); // Align left
      container.add(scene.add.text(countX, currentY + headerHeight / 2, "PLAYERS", headerTextStyle).setOrigin(0.5));
      container.add(scene.add.text(scoreX, currentY + headerHeight / 2, "SCORE", headerTextStyle).setOrigin(1, 0.5)); // Align right

      currentY += headerHeight + 5;
  } catch (e) { console.error("Error creating team header:", e); currentY += headerHeight + 5; }

  // --- Rows ---
  const displayScores = scores ? scores.slice(0, maxRows) : [];
  if (displayScores.length === 0) {
      try { container.add(scene.add.text(0, currentY + 20, "No team scores available.", { fontSize: '16px', color: '#888888' }).setOrigin(0.5)); currentY += 40; }
      catch(e) { console.error("Error adding 'no team scores' text:", e); }
  } else {
      displayScores.forEach((score, index) => {
         try {
              const rowY = currentY + rowHeight / 2;
              const isCurrentTeam = score.isCurrentGame || (this.gameCode && score.gameCode === this.gameCode);
              let bgColor = index % 2 === 0 ? 0x281030 : 0x301540; let strokeColor = 0x402060; let strokeThickness = 1;
              if (isCurrentTeam) { bgColor = 0x502568; strokeColor = MenuUIUtils.COLORS.accent3; strokeThickness = 1.5; }

              const rowBg = scene.add.rectangle(0, rowY, tableWidth, rowHeight - 4, bgColor, 0.9).setStrokeStyle(strokeThickness, strokeColor, 0.8).setOrigin(0.5);
              container.add(rowBg);

              // Rank (Centered)
              let rankColor = isCurrentTeam ? (MenuUIUtils.COLORS.accent3_light || MenuUIUtils.COLORS.accent3) : '#FFFFFF';
              if (index === 0 && !isCurrentTeam) rankColor = '#FFD700'; else if (index === 1 && !isCurrentTeam) rankColor = '#C0C0C0'; else if (index === 2 && !isCurrentTeam) rankColor = '#CD7F32';
              container.add(scene.add.text(rankX, rowY, `${score.rank || index + 1}`, { fontSize: '16px', color: rankColor, fontStyle: 'bold' }).setOrigin(0.5));

              // Team Name (Left Aligned)
              let teamName = score.teamName || 'Team'; const nameLengthLimit = 20; if (teamName.length > nameLengthLimit) teamName = teamName.substring(0, nameLengthLimit - 1) + '…';
              const star = isCurrentTeam ? ' ★' : '';
              container.add(scene.add.text(nameX, rowY, teamName + star, {
                      fontSize: '16px', color: isCurrentTeam ? (MenuUIUtils.COLORS.accent3_light || MenuUIUtils.COLORS.accent3) : '#FFFFFF',
                      fontStyle: isCurrentTeam ? 'bold' : 'normal' }).setOrigin(0, 0.5)); // Align left

              // Player Count (Centered)
              container.add(scene.add.text(countX, rowY, `${score.playerCount || '?'}`, { fontSize: '16px', color: '#AAAAAA' }).setOrigin(0.5));

              // Score (Right Aligned)
              container.add(scene.add.text(scoreX, rowY, (score.score || 0).toLocaleString(), { fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold' }).setOrigin(1, 0.5)); // Align right

              currentY += rowHeight;
          } catch(rowError) { console.error(`Error creating team score row ${index}:`, rowError); }
      });
  }
  return currentY - startY; // Return total height
}
// Replace the entire createAlignedTeamScoreList method with this:



    animateGlow(target, scaleTo = 1.05, alphaTo = 0.4, duration = 1800) {
        if (this.tweens && target && target.active) {
             this.tweens.add({
                 targets: target,
                 alpha: { from: 0.15, to: alphaTo },
                 scale: { from: 1, to: scaleTo },
                 duration: duration,
                 yoyo: true,
                 repeat: -1,
                 ease: 'Sine.easeInOut'
             });
        }
    }


    fallbackGameOverDisplay(message = "GAME OVER") {
         try {
             if(this.elements.contentContainer) this.elements.contentContainer.removeAll(true);
             if(this.elements.leaderboardContainer) this.elements.leaderboardContainer.removeAll(true);
             if(this.elements.achievementContainer) this.elements.achievementContainer.removeAll(true);

             const width = this.cameras.main.width;
             const height = this.cameras.main.height;
             const centerX = width / 2;

             const errorText = this.createThemedText(centerX, height * 0.3, message, 'sizeXl', MenuUIUtils.COLORS.danger);
             const scoreText = this.createThemedText(centerX, height * 0.4, `Final Score: ${this.score.toLocaleString()}`, 'sizeLg', MenuUIUtils.COLORS.textLight);

             this.createReturnButton(height * 0.6); // Add button below messages

             this.elements.errorFallbackText = errorText;
             this.elements.errorFallbackScore = scoreText;

         } catch (e) {
              console.error("Critical error during fallback display:", e);
              // Absolute last resort - maybe refresh?
              // location.reload();
         }
     }

    shutdown() {
        console.log(`[GameOverScene] Shutting down...`);
        // Stop timers
        if (this.updateTimer) { this.updateTimer.remove(); this.updateTimer = null; }
        // Remove tweens specifically associated with this scene's elements if possible
        if (this.achievementPulseEffect) { this.achievementPulseEffect.stop(); this.achievementPulseEffect = null; }
        if (this.elements.achievementGlow) this.tweens?.killTweensOf(this.elements.achievementGlow);
        if (this.elements.headerText) this.tweens?.killTweensOf(this.elements.headerText);
        // Remove all elements created and stored in this.elements
        Object.values(this.elements).forEach(el => {
            if (el && el.destroy) {
                try { el.destroy(true); } // Destroy containers and their children
                catch (e) { console.warn("Error destroying element:", e); }
            }
        });
        this.elements = {};
        // Ensure base class shutdown runs last
        super.shutdown();
        console.log(`[GameOverScene] Shutdown complete.`);
    }
}